/**
 * sharedRegions — the site-wide HEADER and FOOTER.
 *
 * Both are singleton grids (`grid:__header`, `grid:__footer`) in the site Yjs
 * doc, structured like a section's nested grid (they hold widgets). Every page
 * node renders them as bands above/below its own sections. Because the grid
 * store is a module singleton and Yjs binds ONE map per id, every page renders
 * the SAME store entry — so editing the header on any page updates it on all
 * pages, with no cross-page sync code. This hook binds + seeds them ONCE
 * (call it from the canvas, not per page node).
 */
import { useEffect, useRef } from "react";
import type { Doc as YDoc } from "yjs";
import { useGridStore } from "@fredericrous/grid";
import { bindYjsToGrid } from "@website-builder/y-grid";
import { SECTION_GRID_CONFIG } from "./sectionInsert.js";
import { useEditorContext } from "./EditorContext.js";

export const HEADER_GRID_ID = "__header";
export const FOOTER_GRID_ID = "__footer";

// FIXED ids for the singleton header/footer default items (exactly one logo,
// one nav, one footer text per site). Using stable ids — not `generateId` —
// makes seeding IDEMPOTENT: if two contexts seed concurrently (a leader plus a
// follower whose sync arrived late — see useEditorDoc's follower-fallback), both
// write the SAME map keys, so the CRDT converges to one item instead of merging
// two random-id copies into a doubled header/footer. Stable + globally unique
// (generateId output is `<prefix>_<rand>_<n>`, so these never collide).
const HEADER_LOGO_ID = "__header-logo";
const HEADER_NAV_ID = "__header-nav";
const FOOTER_TEXT_ID = "__footer-text";

function seedHeader(gridId: string): void {
  const store = useGridStore.getState();
  // Logo (image) — top-left; movable on the grid like any widget.
  store.addItem(gridId, {
    id: HEADER_LOGO_ID,
    position: { x: 0, y: 0, w: 3, h: 2 },
    constraints: {},
    contentType: "image",
    contentProps: {},
  });
  // The page-links menu — to its right; also movable.
  store.addItem(gridId, {
    id: HEADER_NAV_ID,
    position: { x: 3, y: 0, w: 9, h: 2 },
    constraints: {},
    contentType: "nav",
    contentProps: { locked: true },
  });
}

/** Bring an already-seeded header up to the current default: ensure a logo image
 *  exists (headers seeded before logos had only the nav). Grid compaction settles
 *  placement; the user can drag to arrange. Uses the FIXED logo id so two
 *  contexts running this fixup converge on one logo (was the documented
 *  "addItem mints a fresh id → no dedup" duplication). */
function ensureHeaderLogo(gridId: string): void {
  const store = useGridStore.getState();
  const grid = store.grids[gridId];
  if (!grid) return;
  const items = Object.values(grid.items);
  if (items.length === 0) return; // fresh → seedHeader handles it
  if (items.some((it) => it.contentType === "image")) return;
  store.addItem(gridId, {
    id: HEADER_LOGO_ID,
    position: { x: 0, y: 0, w: 3, h: 2 },
    constraints: {},
    contentType: "image",
    contentProps: {},
  });
}

/** Remove any nav from an already-seeded footer — the page-links menu is a
 *  header-only thing (footers seeded before this had a nav). */
function ensureFooterNoNav(gridId: string): void {
  const store = useGridStore.getState();
  const grid = store.grids[gridId];
  if (!grid) return;
  for (const [id, it] of Object.entries(grid.items)) {
    if (it.contentType === "nav") store.removeItem(gridId, id);
  }
}

function seedFooter(gridId: string): void {
  const store = useGridStore.getState();
  // A copyright line (editable text widget). The page-links menu is a HEADER
  // thing — not seeded into the footer. Fixed id → idempotent seed.
  store.addItem(gridId, {
    id: FOOTER_TEXT_ID,
    position: { x: 0, y: 0, w: 12, h: 1 },
    constraints: {},
    contentType: "text",
    contentProps: {},
  });
}

const REGIONS = [
  [HEADER_GRID_ID, seedHeader],
  [FOOTER_GRID_ID, seedFooter],
] as const;

/** Site-level settings map (theme, nav, …) — holds the seeded marker. */
const SITE = "site";
/** Set once the header/footer defaults have been written. */
export const REGIONS_SEEDED_KEY = "regionsSeeded";
/** Origin of the marker write: not FLOW/GRID, so the UndoManager ignores it. */
export const REGIONS_SEEDED_ORIGIN = Symbol("regions-seeded");

/**
 * Run `seed` (the default header/footer + the legacy fixups) unless this site
 * already did, then record it. The defaults are the regions' BIRTH, not a
 * state to restore: without the marker, a header emptied on purpose (by hand
 * or through builder-mcp) grew its logo and menu back on the next load, and a
 * deleted logo came back through ensureHeaderLogo. Sites seeded before the
 * marker existed run the fixups one last time, then get it.
 */
export function seedRegionsOnce(ydoc: YDoc, seed: () => void): void {
  const site = ydoc.getMap<unknown>(SITE);
  if (site.get(REGIONS_SEEDED_KEY) === true) return;
  seed();
  ydoc.transact(
    () => site.set(REGIONS_SEEDED_KEY, true),
    REGIONS_SEEDED_ORIGIN,
  );
}

/**
 * Bind + seed the shared header/footer grids ONCE. Idempotent per canvas:
 * imports any persisted content, seeds defaults only when empty and never
 * seeded before (seedRegionsOnce). No-op in
 * read-only (version preview): the store is pre-seeded from the snapshot.
 *
 * `seedDefault` (EditorDoc.canSeed) gates ALL writes here — seeding AND the
 * additive fixups (ensureHeaderLogo/ensureFooterNoNav). A non-authoritative
 * FOLLOWER binds (renders synced content) but must never mutate: seeding or a
 * fixup on a follower would broadcast a duplicate logo/nav into the shared doc
 * (the leader does the same fixup, and addItem mints a fresh id → no dedup).
 */
export function useSharedRegions(
  ydoc: YDoc,
  readOnly: boolean,
  seedDefault = true,
): void {
  const { undoManager } = useEditorContext();
  const done = useRef(false);
  // Read at bind time only: a later flip must not rebind the regions.
  const seedRef = useRef(seedDefault);
  seedRef.current = seedDefault;
  useEffect(() => {
    if (readOnly || done.current) return;
    done.current = true;
    const bindings = REGIONS.map(([gid]) => {
      const store = useGridStore.getState();
      // Create the shell if missing — the y-grid late-joiner needs the config
      // to import. Cross-draft hygiene ("__header"/"__footer" are fixed ids
      // shared across ALL drafts) now lives at the draft-open call sites: the
      // singleton store is factory-reset once per draft open (resetGridStore
      // in the web + native EditorScreens) before any binding mounts, so a
      // pre-existing entry here can only be this draft's own.
      if (!store.grids[gid])
        store.importLayout(gid, { config: SECTION_GRID_CONFIG, items: [] });
      return bindYjsToGrid(ydoc, gid);
    });
    // Only the authoritative tab seeds/migrates, and only once per site; a
    // follower just renders.
    if (seedRef.current) {
      seedRegionsOnce(ydoc, () => {
        for (const [gid, seed] of REGIONS) {
          const grid = useGridStore.getState().grids[gid];
          if (grid && Object.keys(grid.items).length === 0) seed(gid);
          else if (gid === HEADER_GRID_ID) ensureHeaderLogo(gid);
          else if (gid === FOOTER_GRID_ID) ensureFooterNoNav(gid);
        }
      });
    }
    // Track for undo AFTER seeding/migration (which write synchronously), so a
    // brand-new site doesn't open with the seed as an undoable action — while
    // later logo/menu moves + resizes ARE undoable.
    for (const [gid] of REGIONS)
      undoManager.addToScope(ydoc.getMap(`grid:${gid}`));
    // The initial load must not open as an undoable "action". Seeding + the node
    // hydrate push are already kept out of the history (addToScope-after-seed +
    // HYDRATE_ORIGIN); this also clears the async settling that lands right after
    // — section auto-grow re-fitting heights once content (Lexical, images)
    // measures. One clear past the capture window; real edits come later.
    const clearTimer = window.setTimeout(() => undoManager.clear(), 800);
    return () => {
      window.clearTimeout(clearTimer);
      bindings.forEach((b) => b.destroy());
      done.current = false;
    };
  }, [ydoc, readOnly, undoManager]);
}
